import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Bot, CheckCircle2, XCircle, Quote } from 'lucide-react';
import { useApi } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1B7A3D', teal: '#0E7C7B', red: '#B0413E', amber: '#8A6D1D', line: '#E4E9EF' };
const FAILURE_TYPES = ['claim_denied_upheld', 'claim_denied_overturned', 'underinsurance', 'non_disclosure', 'exclusion_applied', 'process_failure', 'quantum_dispute', 'other'];
const STATUS = { proposed: { bg: '#EAF1FB', fg: '#2A6FC4' }, confirmed: { bg: '#E8F4EC', fg: '#1B7A3D' }, rejected: { bg: '#FBEBEA', fg: '#B0413E' } };

function TagEditor({ det, lines, api, onDone }) {
  const p = det.proposal || {};
  const [tags, setTags] = useState({
    line: p.line_valid ? p.line : '', segment: p.segment || '', hazard: p.hazard || '',
    wording_clause: p.wording_clause || '', failure_type: p.failure_type || 'other', summary: p.summary || '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setTags(t => ({ ...t, [k]: v }));
  const cits = p.citations || {};

  const confirm = async () => {
    if (!tags.line) return toast.error('Choose a line');
    if (!tags.hazard || !tags.summary) return toast.error('Hazard and summary are required');
    setBusy(true);
    try {
      const r = await api.post(`/dean/afca/determinations/${det.det_id}/confirm`, {
        ...tags, segment: tags.segment || null, wording_clause: tags.wording_clause || null,
      });
      const c = r.data.citation;
      toast.success(`Written to Layer 4 — pack ${c.line} v${c.pack_version} (entry ${c.entry_id})${r.data.corrected ? ' · corrected' : ' · accepted as proposed'}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Confirm failed'); }
    setBusy(false);
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/dean/afca/determinations/${det.det_id}/reject`, { note: null });
      toast.success('Determination rejected — nothing written'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
    setBusy(false);
  };

  const Cite = ({ k }) => cits[k] ? (
    <div className="mt-0.5 flex items-start gap-1 text-[10px] italic" style={{ color: p.citation_checks?.[k] ? C.teal : C.amber }}>
      <Quote size={9} className="mt-0.5 shrink-0" /> "{cits[k]}"{p.citation_checks?.[k] === false && ' (not verbatim)'}
    </div>
  ) : null;

  return (
    <div className="p-4 border-t space-y-3" style={{ borderColor: C.line, background: '#FAFBFC' }} data-testid={`tag-editor-${det.det_id}`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slate }}>Line {!p.line_valid && p.line && <span style={{ color: C.red }}>(Dean proposed "{p.line}" — invalid)</span>}</div>
          <select value={tags.line} onChange={e => set('line', e.target.value)} className="w-full border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid={`tag-line-${det.det_id}`}>
            <option value="">Select…</option>
            {lines.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <Cite k="line" />
        </div>
        <div>
          <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slate }}>Segment</div>
          <input value={tags.segment} onChange={e => set('segment', e.target.value)} className="w-full border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid={`tag-segment-${det.det_id}`} />
        </div>
        <div>
          <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slate }}>Hazard</div>
          <input value={tags.hazard} onChange={e => set('hazard', e.target.value)} className="w-full border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid={`tag-hazard-${det.det_id}`} />
          <Cite k="hazard" />
        </div>
        <div>
          <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slate }}>Wording clause</div>
          <input value={tags.wording_clause} onChange={e => set('wording_clause', e.target.value)} className="w-full border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid={`tag-clause-${det.det_id}`} />
          <Cite k="wording_clause" />
        </div>
        <div>
          <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slate }}>Failure type</div>
          <select value={tags.failure_type} onChange={e => set('failure_type', e.target.value)} className="w-full border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid={`tag-failure-${det.det_id}`}>
            {FAILURE_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <Cite k="failure_type" />
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slate }}>Lesson (written to Layer 4)</div>
        <textarea value={tags.summary} onChange={e => set('summary', e.target.value)} rows={2} className="w-full border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid={`tag-summary-${det.det_id}`} />
      </div>
      <div className="flex gap-2">
        <button onClick={confirm} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold text-white disabled:opacity-50" style={{ background: C.green }} data-testid={`confirm-det-${det.det_id}`}>
          <CheckCircle2 size={13} /> Confirm → Layer 4
        </button>
        <button onClick={reject} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold border disabled:opacity-50" style={{ borderColor: C.red, color: C.red }} data-testid={`reject-det-${det.det_id}`}>
          <XCircle size={13} /> Reject
        </button>
      </div>
    </div>
  );
}

export default function AfcaTaggerPage() {
  const api = useApi();
  const [dets, setDets] = useState([]);
  const [lines, setLines] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ title: '', reference: '', decision_date: '', text: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, m, s] = await Promise.all([
        api.get('/dean/afca/determinations'), api.get('/knowledge/layers'), api.get('/dean/stats')]);
      setDets(d.data.determinations); setLines(m.data.lines); setStats(s.data);
    } catch { /* noop */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (form.title.length < 3) return toast.error('Title required');
    if (form.text.length < 100) return toast.error('Paste the determination text (min 100 chars)');
    setSubmitting(true);
    try {
      await api.post('/dean/afca/determinations', {
        title: form.title, reference: form.reference || null,
        decision_date: form.decision_date || null, text: form.text,
      });
      toast.success('Dean proposed tags — review below');
      setForm({ title: '', reference: '', decision_date: '', text: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Tagging failed'); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4" data-testid="afca-tagger-page">
      <div className="p-4 rounded-lg border" style={{ borderColor: C.line, background: '#0D1B2A' }}>
        <div className="flex items-center gap-2 text-white text-sm font-semibold"><Bot size={16} style={{ color: '#4ADE80' }} /> Dean v1 — AFCA dispute-record tagger (ninth archetype, propose-only)</div>
        <div className="text-xs mt-1" style={{ color: '#9FB1C4' }}>
          Paste a manually retrieved determination. Dean proposes tags with citations; you confirm or correct; the confirmed record is written to Layer 4 by deterministic code. Dean has no write access to packs. Bulk ingest only if the 8 Sep terms-of-use answer permits.
        </div>
        {stats && stats.total > 0 && (
          <div className="flex gap-4 mt-2 text-[11px]" style={{ color: '#9FB1C4' }} data-testid="dean-stats">
            <span>{stats.total} tagged</span><span>{stats.confirmed} confirmed</span><span>{stats.corrected} corrected</span><span>{stats.rejected} rejected</span>
            {stats.acceptance_rate !== null && <span style={{ color: '#4ADE80' }}>acceptance {(stats.acceptance_rate * 100).toFixed(0)}%</span>}
          </div>
        )}
      </div>

      <div className="p-4 rounded-lg border space-y-3" style={{ borderColor: C.line, background: '#fff' }} data-testid="afca-submit-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Title (e.g. Case 1054321 — storm damage denial)" className="border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid="afca-title-input" />
          <input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="AFCA reference (optional)" className="border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid="afca-reference-input" />
          <input type="date" value={form.decision_date} onChange={e => setForm(f => ({ ...f, decision_date: e.target.value }))} className="border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid="afca-date-input" />
        </div>
        <textarea value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))} rows={6} placeholder="Paste the determination text here…" className="w-full border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid="afca-text-input" />
        <button onClick={submit} disabled={submitting} className="px-4 py-2 rounded-md text-xs font-semibold text-white disabled:opacity-50" style={{ background: C.navy }} data-testid="afca-submit-btn">
          {submitting ? 'Dean is tagging…' : 'Submit to Dean'}
        </button>
      </div>

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.line, background: '#fff' }}>
        {dets.length === 0 && <div className="p-6 text-center text-sm" style={{ color: C.slate }} data-testid="afca-empty">No determinations yet — paste the first one above.</div>}
        {dets.map(d => (
          <div key={d.det_id} className="border-b last:border-b-0" style={{ borderColor: C.line }}>
            <div className="flex items-center gap-3 px-4 py-3" data-testid={`det-row-${d.det_id}`}>
              <span className="text-sm font-semibold" style={{ color: C.navy }}>{d.title}</span>
              {d.reference && <span className="text-xs" style={{ color: C.slate }}>{d.reference}</span>}
              <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase" style={{ background: STATUS[d.status]?.bg, color: STATUS[d.status]?.fg }}>{d.status}</span>
              {d.status === 'confirmed' && d.layer4_citation && (
                <span className="ml-auto text-[11px]" style={{ color: C.green }} data-testid={`det-citation-${d.det_id}`}>
                  → L4 · {d.layer4_citation.line} v{d.layer4_citation.pack_version} · entry {d.layer4_citation.entry_id}{d.corrected ? ' · corrected' : ''}
                </span>
              )}
            </div>
            {d.status === 'proposed' && <TagEditor det={d} lines={lines} api={api} onDone={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}
